export const ATTACHMENT_MAX_COUNT = 6;
export const ATTACHMENT_MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

export const ATTACHMENT_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const ATTACHMENT_ACCEPT = ATTACHMENT_ALLOWED_MIME_TYPES.join(',');

/** Same messages as Sales MultiFileAttachment */
export const ATTACHMENT_VALIDATION_MESSAGES = {
  unsupportedFormat: 'Unsupported file format. Please upload a PDF, JPG, JPEG, PNG, DOC, DOCX, XLS, or XLSX file.',
  maxAttachments: 'You cannot add more than 6 attachments',
  fileSizeExceeded:
    'File size exceeds the maximum limit of 5 MB. Please upload a smaller file.',
  duplicateFile: 'This file has already been attached.',
  uploadFailure: 'Unable to upload the file. Please try again.',
  oversizedHeading: 'The following file(s) exceed the 5 MB limit:',
  duplicateHeading: 'The following file(s) are already attached:',
};

function formatNamedFileList(heading, fileNames) {
  if (!fileNames.length) {
    return null;
  }
  const numbered = fileNames.map((name, index) => `${index + 1}. ${name}`).join('\n');
  return `${heading}\n${numbered}`;
}

/**
 * Validates selected files the same way as Sales document attachments.
 * Messages match Sales; valid files are still added when some fail size/duplicate checks.
 */
export function validateAttachmentSelection(files, existingAttachments = []) {
  const fileList = Array.from(files || []);
  if (fileList.length === 0) {
    return { validFiles: [], errorMessage: null, duplicateMessage: null };
  }

  // Sales: any unsupported format blocks the whole selection
  const hasUnsupported = fileList.some((file) => !ATTACHMENT_ALLOWED_MIME_TYPES.includes(file.type));
  if (hasUnsupported) {
    return {
      validFiles: [],
      errorMessage: ATTACHMENT_VALIDATION_MESSAGES.unsupportedFormat,
      duplicateMessage: null,
    };
  }

  // Sales: existing + selected count > 6 blocks the whole selection
  if (existingAttachments.length + fileList.length > ATTACHMENT_MAX_COUNT) {
    return {
      validFiles: [],
      errorMessage: ATTACHMENT_VALIDATION_MESSAGES.maxAttachments,
      duplicateMessage: null,
    };
  }

  const validFiles = [];
  const seenNames = new Set(existingAttachments.map((attachment) => attachment.documentname));
  const oversizedNames = [];
  const duplicateNames = [];

  for (const file of fileList) {
    if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
      oversizedNames.push(file.name);
      continue;
    }
    if (seenNames.has(file.name)) {
      duplicateNames.push(file.name);
      continue;
    }

    seenNames.add(file.name);
    validFiles.push(file);
  }

  const errorParts = [
    formatNamedFileList(ATTACHMENT_VALIDATION_MESSAGES.oversizedHeading, oversizedNames),
    formatNamedFileList(ATTACHMENT_VALIDATION_MESSAGES.duplicateHeading, duplicateNames),
  ].filter(Boolean);

  return {
    validFiles,
    errorMessage: errorParts.length > 0 ? errorParts.join('\n\n') : null,
    duplicateMessage: formatNamedFileList(
      ATTACHMENT_VALIDATION_MESSAGES.duplicateHeading,
      duplicateNames,
    ),
  };
}

/** Returns true when any pending local file exceeds 5 MB. */
export function hasOversizedAttachment(attachments = []) {
  return attachments.some(
    (attachment) => attachment?.file && attachment.file.size > ATTACHMENT_MAX_SIZE_BYTES,
  );
}

/** Sales-style warning for pending oversized local files. */
export function getOversizedAttachmentMessage(attachments = []) {
  const oversizedNames = attachments
    .filter((attachment) => attachment?.file && attachment.file.size > ATTACHMENT_MAX_SIZE_BYTES)
    .map((attachment) => attachment.documentname || attachment.file?.name)
    .filter(Boolean);

  return formatNamedFileList(ATTACHMENT_VALIDATION_MESSAGES.oversizedHeading, oversizedNames);
}
